import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { Star, Trophy, Gamepad2, RefreshCw, Loader2, CheckCircle, ArrowRight, Search, ArrowUpDown, SortAsc, SortDesc, Lock, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import PlatformBadge, { SystemBadge } from "@/components/PlatformBadge";
import { AdBanner } from "@/components/AdSense";
import ProGate from "@/components/ProGate";

const API = `${getApiUrl()}/api`;

const SORT_OPTIONS = [
  { value: "name_asc", label: "Name (A-Z)" },
  { value: "name_desc", label: "Name (Z-A)" },
  { value: "completion_desc", label: "Completion (High-Low)" },
  { value: "completion_asc", label: "Completion (Low-High)" },
  { value: "score_desc", label: "Score (High-Low)" },
  { value: "score_asc", label: "Score (Low-High)" },
  { value: "date_added_desc", label: "Recently Added" },
  { value: "date_added_asc", label: "Oldest Added" },
];

export default function Priorities() {
  const [games, setGames] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingCompleted, setCheckingCompleted] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("name_asc");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [gamesRes, platformsRes, prefsRes] = await Promise.all([
        axios.get(`${API}/games/priorities`),
        axios.get(`${API}/platforms`),
        axios.get(`${API}/settings/preferences`),
      ]);
      setGames(gamesRes.data);
      setPlatforms(platformsRes.data);
      if (prefsRes.data.priorityGamesSort) {
        setSortOrder(prefsRes.data.priorityGamesSort);
      }
    } catch (error) {
      console.error("Failed to fetch priority games:", error);
      toast.error("Failed to load priority games");
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort games
  const filteredAndSortedGames = useMemo(() => {
    let result = [...games];
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(game => 
        game.name?.toLowerCase().includes(query) ||
        game.developer?.toLowerCase().includes(query) ||
        game.publisher?.toLowerCase().includes(query)
      );
    }
    
    // Sort
    result.sort((a, b) => {
      switch (sortOrder) {
        case "name_asc":
          return (a.name || "").localeCompare(b.name || "");
        case "name_desc":
          return (b.name || "").localeCompare(a.name || "");
        case "completion_asc":
          return (a.completionPct || 0) - (b.completionPct || 0);
        case "completion_desc":
          return (b.completionPct || 0) - (a.completionPct || 0);
        case "score_asc":
          return (a.earnedScore || 0) - (b.earnedScore || 0);
        case "score_desc":
          return (b.earnedScore || 0) - (a.earnedScore || 0);
        case "date_added_asc":
          return new Date(a.priorityAddedAt || a.createdAt || 0) - new Date(b.priorityAddedAt || b.createdAt || 0);
        case "date_added_desc":
          return new Date(b.priorityAddedAt || b.createdAt || 0) - new Date(a.priorityAddedAt || a.createdAt || 0);
        default:
          return 0;
      }
    });
    
    return result;
  }, [games, searchQuery, sortOrder]);

  const handleSortChange = async (newSort) => {
    setSortOrder(newSort);
    // Save preference
    try {
      await axios.put(`${API}/settings/preferences`, { priorityGamesSort: newSort });
    } catch (error) {
      console.error("Failed to save sort preference:", error);
    }
  };

  const handleCheckCompleted = async () => {
    setCheckingCompleted(true);
    try {
      const response = await axios.post(`${API}/games/check-completed-priorities`);
      const { checked, completedCount, completedGames, lockedSkipped = 0 } = response.data;
      
      if (completedCount > 0) {
        const lockedNote = lockedSkipped > 0
          ? ` (${lockedSkipped} locked game${lockedSkipped > 1 ? 's' : ''} kept)`
          : '';
        toast.success(
          `${completedCount} game${completedCount > 1 ? 's' : ''} completed and removed from priorities: ${completedGames.map(g => g.name).join(', ')}${lockedNote}`
        );
        fetchData();
      } else {
        const lockedNote = lockedSkipped > 0
          ? ` (${lockedSkipped} locked game${lockedSkipped > 1 ? 's' : ''} skipped)`
          : '';
        toast.info(`Checked ${checked} games - none completed yet. Keep going!${lockedNote}`);
      }
    } catch (error) {
      console.error("Failed to check completed games:", error);
      toast.error("Failed to check completed games");
    } finally {
      setCheckingCompleted(false);
    }
  };

  const handleRemovePriority = async (gameId) => {
    try {
      await axios.post(`${API}/games/${gameId}/priority`);
      toast.success("Removed from priorities");
      fetchData();
    } catch (error) {
      console.error("Failed to remove priority:", error);
      toast.error("Failed to remove from priorities");
    }
  };

  const handleToggleLock = async (gameId) => {
    try {
      const { data } = await axios.post(`${API}/games/${gameId}/priority-lock`);
      setGames((prev) =>
        prev.map((g) => (g.id === gameId ? { ...g, priorityLocked: data.priorityLocked } : g))
      );
      toast.success(data.priorityLocked ? "Locked — won't be auto-removed" : "Unlocked");
    } catch (error) {
      console.error("Failed to toggle lock:", error);
      toast.error("Failed to update lock");
    }
  };

  const getPlatformColor = (platformId) => {
    const platform = platforms.find((p) => p.id === platformId);
    return platform?.color || "#A1A1AA";
  };

  const getScoreAbbr = (platformId) => {
    const platform = platforms.find((p) => p.id === platformId);
    return platform?.scoreAbbr || "XP";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <ProGate feature="priorities">
    <div data-testid="priorities-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight flex items-center gap-3">
            <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
            Priority Games
          </h1>
          <p className="text-white/50 mt-1">
            {filteredAndSortedGames.length} of {games.length} game{games.length !== 1 ? 's' : ''} you want to focus on
          </p>
        </div>

        <Button
          onClick={handleCheckCompleted}
          disabled={checkingCompleted || games.length === 0}
          variant="outline"
          className="border-green-500/30 text-green-400 hover:bg-green-500/10"
          data-testid="check-completed-btn"
        >
          {checkingCompleted ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Check for Completed
            </>
          )}
        </Button>
      </div>

      {/* Search and Sort Controls */}
      {games.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search priority games..."
              className="pl-10 bg-[#121212] border-white/10"
              data-testid="priority-search"
            />
          </div>
          
          {/* Sort */}
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-white/40" />
            <Select value={sortOrder} onValueChange={handleSortChange}>
              <SelectTrigger className="w-48 bg-[#121212] border-white/10" data-testid="priority-sort">
                <SelectValue placeholder="Sort by..." />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                {SORT_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Empty State */}
      {games.length === 0 && (
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="py-12 text-center">
            <Star className="w-16 h-16 text-white/10 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No Priority Games</h2>
            <p className="text-white/50 mb-4 max-w-md mx-auto">
              Mark games as priority from their detail page to see them here. 
              Priority games help you focus on what you want to complete next.
            </p>
            <Link to="/games">
              <Button className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold">
                <Gamepad2 className="w-4 h-4 mr-2" />
                Browse Games
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* No Results State */}
      {games.length > 0 && filteredAndSortedGames.length === 0 && (
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="py-12 text-center">
            <Search className="w-16 h-16 text-white/10 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No Matching Games</h2>
            <p className="text-white/50 mb-4">
              No priority games match "{searchQuery}"
            </p>
            <Button 
              variant="outline" 
              onClick={() => setSearchQuery("")}
              className="border-white/20"
            >
              Clear Search
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Priority Games Grid */}
      {filteredAndSortedGames.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAndSortedGames.map((game) => (
            <Card
              key={game.id}
              data-testid={`priority-game-${game.id}`}
              className={`bg-[#0A0A0A] border-white/5 transition-all group relative overflow-hidden ${
                game.priorityLocked ? "border-yellow-500/40 ring-1 ring-yellow-500/20" : "hover:border-yellow-500/30"
              }`}
            >
              {/* Top-right controls: lock + remove */}
              <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleToggleLock(game.id);
                  }}
                  className={`p-1.5 rounded-full transition-colors ${
                    game.priorityLocked
                      ? "bg-yellow-500/30 hover:bg-yellow-500/40"
                      : "bg-white/5 hover:bg-white/10"
                  }`}
                  title={game.priorityLocked ? "Locked — won't be auto-removed. Click to unlock" : "Lock to prevent auto-removal"}
                  data-testid={`toggle-lock-${game.id}`}
                >
                  {game.priorityLocked ? (
                    <Lock className="w-4 h-4 text-yellow-400" />
                  ) : (
                    <Unlock className="w-4 h-4 text-white/40" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleRemovePriority(game.id);
                  }}
                  className="p-1.5 rounded-full bg-yellow-500/20 hover:bg-red-500/20 transition-colors"
                  title="Remove from priorities"
                  data-testid={`remove-priority-${game.id}`}
                >
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 group-hover:text-red-400 group-hover:fill-red-400 transition-colors" />
                </button>
              </div>

              <Link to={`/games/${game.id}`}>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Box Art */}
                    <div className="w-20 h-28 bg-[#121212] rounded-lg overflow-hidden shrink-0">
                      {game.boxArtUrl ? (
                        <img
                          src={game.boxArtUrl}
                          alt={game.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Gamepad2 className="w-8 h-8 text-white/10" />
                        </div>
                      )}
                    </div>

                    {/* Game Info */}
                    <div className="flex-1 min-w-0 space-y-2">
                      <h3 className="font-semibold text-white truncate group-hover:text-yellow-400 transition-colors flex items-center gap-1.5">
                        {game.priorityLocked && (
                          <Lock className="w-3.5 h-3.5 text-yellow-400 shrink-0" title="Locked" />
                        )}
                        <span className="truncate">{game.name}</span>
                      </h3>
                      
                      <div className="flex items-center gap-2">
                        <PlatformBadge platform={game.platform} />
                        {game.system && (
                          <SystemBadge system={game.system} platform={game.platform} />
                        )}
                      </div>

                      {/* Progress */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-white/50">
                            {game.unlockedAchievements}/{game.totalAchievements} achievements
                          </span>
                          <span className="text-white font-medium">
                            {game.completionPct}%
                          </span>
                        </div>
                        <Progress 
                          value={game.completionPct} 
                          className="h-2"
                        />
                      </div>

                      {/* Score */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-yellow-400 font-semibold">
                          {game.earnedScore}/{game.totalScore} {getScoreAbbr(game.platform)}
                        </span>
                        {game.dlcCount > 0 && (
                          <span className="text-white/40 text-xs">
                            +{game.dlcCount} DLC{game.dlcCount > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* View Game Link */}
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-end">
                    <span className="text-white/40 text-sm flex items-center gap-1 group-hover:text-yellow-400 transition-colors">
                      View Game <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </CardContent>
              </Link>
            </Card>
          ))}
        </div>
      )}

      {/* Info Note */}
      {games.length > 0 && (
        <div className="p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
          <p className="text-yellow-400/70 text-sm">
            <Star className="w-4 h-4 inline mr-2 fill-yellow-400" />
            <strong>Tip:</strong> Games are automatically removed from priorities when you reach 100% completion.
            Click &quot;Check for Completed&quot; to scan all priority games. Use the <Lock className="w-3.5 h-3.5 inline mx-0.5" /> lock
            on any game you never want auto-removed — locked games stay put even at 100%.
          </p>
        </div>
      )}

      {/* AdSense */}
      <AdBanner />
    </div>
    </ProGate>
  );
}
